import type { HTMLAttributes, RefObject } from 'react';
import { useEffect, useRef } from 'react';
import { useHoverDirty } from 'react-use';

import { cn } from '~/lib/cn';

import { BACKGROUND_COLORS } from './constants';
import { arraySum } from './utils';

interface VerticalBarProps extends HTMLAttributes<HTMLDivElement> {
  percentageValues: number[];
  className?: string;
  barActive?: RefObject<HTMLDivElement>;
  setBarActive: (element?: RefObject<HTMLDivElement>) => void;
  axisLabels: number[];
}

export const VerticalBar = ({
  percentageValues,
  className,
  barActive,
  setBarActive,
  axisLabels,
  ...props
}: VerticalBarProps) => {
  const divRef = useRef<HTMLDivElement>(null);
  const totalPercent = arraySum(percentageValues);
  const isHovering = useHoverDirty(divRef);
  const isBarActive = barActive && barActive !== divRef;
  const isLabelInside = totalPercent >= axisLabels[axisLabels.length - 1] - 5;

  useEffect(() => {
    if (isHovering) {
      setBarActive(divRef);
    } else if (!isHovering && barActive?.current === divRef.current) {
      setBarActive();
    }
  }, [isHovering, divRef, barActive, setBarActive]);

  return (
    <div
      {...props}
      className={cn('flex h-full w-xl bg-level-01', className)}
      ref={divRef}
    >
      <div className="flex grow delay-300 duration-300 ease-out bg-emphasis-default hover:bg-emphasis-subtle">
        <div
          className={cn(
            'flex grow cursor-pointer items-end delay-300 duration-300 ease-out',
            {
              'opacity-stronger': isBarActive,
            },
          )}
        >
          <div
            className="relative flex w-xl grow flex-col-reverse justify-items-center gap-px delay-300 duration-300 ease-out"
            style={{
              height: `${(totalPercent / axisLabels[axisLabels.length - 1]) * 100}%`,
            }}
          >
            {percentageValues.map((percent, index) => {
              return (
                <div
                  key={index}
                  className={cn(
                    'mt-px duration-300 ease-out',
                    BACKGROUND_COLORS[index % BACKGROUND_COLORS.length],
                    {
                      'rounded-t-xs': index === percentageValues.length - 1,
                    },
                  )}
                  style={{
                    height: `${((percent / totalPercent) * 100).toFixed(2)}%`,
                  }}
                />
              );
            })}
            <div
              className={cn(
                'absolute w-full text-center text-caption-md font-semibold',
                isLabelInside
                  ? 'top-0 pt-xs text-strong-on-light'
                  : 'bottom-full pb-xs',
              )}
            >{`${Math.round(totalPercent)}%`}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

VerticalBar.displayName = 'VerticalBar';
